import React from 'react';
import type { DonationAmount } from '../../hooks/useDonationConfig';

interface DonationSummaryProps {
  t: (key: string, fallback: string, params?: Record<string, string | number>) => string;
  donationMode: 'recurring' | 'one-time';
  donateRecurringAmount: DonationAmount | null;
  donateOneTimeAmount: DonationAmount | null;
  donateCustomAmount: string;
  hasRecurringDonation: boolean;
  onToggleRecurring: (checked: boolean) => void;
  onModify: () => void;
}

const DonationSummary: React.FC<DonationSummaryProps> = ({
  t,
  donationMode,
  donateRecurringAmount,
  donateOneTimeAmount,
  donateCustomAmount,
  hasRecurringDonation,
  onToggleRecurring,
  onModify,
}) => {
  const formatRecurring = (amount: DonationAmount): string => {
    if (amount === 'custom') {
      return donateCustomAmount ? `${donateCustomAmount}€` : t('step13c.donation_custom_btn', 'Otra');
    }
    return `${amount}€`;
  };
  const formatOneTime = (amount: DonationAmount): string => {
    if (amount === 'custom') {
      return donateCustomAmount ? `${donateCustomAmount} €` : t('step13c.donation_custom_btn', 'Otra');
    }
    return `${amount} €`;
  };

  const summaryLine = (() => {
    if (donationMode === 'recurring' && donateRecurringAmount !== null && hasRecurringDonation) {
      return t('step13c.donation_summary_recurring', 'Donar {monto}/mes a Protectora de Animales 🐾', {
        monto: formatRecurring(donateRecurringAmount),
      });
    }
    if (donationMode === 'one-time' && donateOneTimeAmount !== null) {
      return t('step13c.donation_summary_onetime', 'Donación única de {monto} a Protectora de Animales 🐾', {
        monto: formatOneTime(donateOneTimeAmount),
      });
    }
    if (hasRecurringDonation) {
      return t('step13c.donation_summary_optional', 'Añadir donación opcional de 1€/mes 🐾');
    }
    return t('step13c.donation_summary_none', 'Sin donación 🐾');
  })();

  const mobileSummaryLine = (() => {
    if (donationMode === 'recurring' && donateRecurringAmount !== null && hasRecurringDonation) {
      return t('step13c.donation_summary_recurring_mobile', 'Donar {monto}/mes', {
        monto: formatRecurring(donateRecurringAmount),
      });
    }
    if (donationMode === 'one-time' && donateOneTimeAmount !== null) {
      return t('step13c.donation_summary_onetime_mobile', 'Donación única de {monto}', {
        monto: formatOneTime(donateOneTimeAmount),
      });
    }
    if (hasRecurringDonation) {
      return t('step13c.donation_summary_optional_mobile', 'Donación refugio 1€/mes');
    }
    return t('step13c.donation_summary_none_mobile', 'Sin donación');
  })();

  const isChecked =
    (donationMode === 'recurring' && donateRecurringAmount !== null) ||
    (donationMode === 'one-time' && donateOneTimeAmount !== null);

  return (
    <div className="flex items-start justify-between gap-3">
      <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => {
            if (donationMode === 'recurring') {
              onToggleRecurring(e.target.checked);
            } else if (e.target.checked) {
              onModify();
            }
          }}
          className="mt-0.5 w-4 h-4 text-[#008BBC] border-gray-300 rounded focus:ring-[#008BBC] cursor-pointer flex-shrink-0"
        />
        <span className="hidden font-poppins text-sm leading-snug text-black/80 sm:inline">{summaryLine}</span>
        <span className="font-poppins text-sm leading-snug text-black/80 sm:hidden">{mobileSummaryLine}</span>
      </label>
      <button
        type="button"
        onClick={onModify}
        className="font-poppins text-xs text-[#008BBC] underline hover:text-[#007aa8] flex-shrink-0"
      >
        <span className="hidden sm:inline">{t('step13c.donation_modify', 'Modificar Donación')}</span>
        <span className="sm:hidden">{t('step13c.donation_modify_mobile', 'Modificar')}</span>
      </button>
    </div>
  );
};

export default DonationSummary;
